import React, { useEffect, useState } from 'react';
import {
  Instagram as InstaIcon,
  Heart,
  MessageCircle,
  ExternalLink,
  Play,
  Loader2,
  Grid3x3,
  BadgeCheck,
} from 'lucide-react';
import {
  INSTAGRAM_POSTS,
  INSTAGRAM_EMBEDS,
  BEHOLD_FEED_ID,
  BEHOLD_POST_COUNT,
  SNAPWIDGET_URL,
  SNAPWIDGET_HEIGHT,
} from '../data/mock';

const IG_URL = 'https://www.instagram.com/hello_fund/';
const IG_HANDLE = 'hello_fund';
const IG_PROFILE = {
  handle: IG_HANDLE,
  name: 'Hello Foundation',
  bio: 'Bringing hope to the less fortunate.\nFeeding 600+ meals a day across Gauteng, South Africa.\nA registered NPO · Est. April 2020\n#HelloFoundation · #BringingHope',
  avatar:
    'https://images.unsplash.com/photo-1509099836639-18ba1795216d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxBZnJpY2FuJTIwY2hpbGRyZW58ZW58MHx8fHwxNzgzMDU5NzA0fDA&ixlib=rb-4.1.0&q=85',
  posts: '312',
  followers: '4,180',
  following: '87',
};

const toEmbedUrl = (url) => {
  try {
    const clean = url.trim().replace(/\/+$/, '');
    const match = clean.match(/instagram\.com\/(p|reel|tv)\/([A-Za-z0-9_-]+)/);
    if (!match) return null;
    return `https://www.instagram.com/${match[1]}/${match[2]}/embed/`;
  } catch {
    return null;
  }
};

const relativeDate = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
    return `${Math.floor(diff / 604800)}w`;
  } catch {
    return '';
  }
};

const InstagramFeed = () => {
  const [hovered, setHovered] = useState(null);
  const [beholdPosts, setBeholdPosts] = useState(null);
  const [loading, setLoading] = useState(!!BEHOLD_FEED_ID);

  useEffect(() => {
    if (!BEHOLD_FEED_ID) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`https://feeds.behold.so/${BEHOLD_FEED_ID}`);
        if (!res.ok) throw new Error('feed');
        const data = await res.json();
        const posts = Array.isArray(data) ? data : data.posts || [];
        if (!cancelled) {
          setBeholdPosts(posts.slice(0, BEHOLD_POST_COUNT));
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const hasBehold = beholdPosts && beholdPosts.length > 0;
  const hasEmbeds = !hasBehold && INSTAGRAM_EMBEDS && INSTAGRAM_EMBEDS.length > 0;
  const embedUrls = hasEmbeds ? INSTAGRAM_EMBEDS.map(toEmbedUrl).filter(Boolean) : [];

  const gridPosts = hasBehold
    ? beholdPosts.map((p) => ({
        id: p.id || p.permalink,
        image: p.sizes?.medium?.mediaUrl || p.mediaUrl || p.thumbnailUrl,
        caption: (p.caption || '').split('\n')[0].slice(0, 140),
        likes: p.likes || p.likesCount || null,
        comments: p.comments || p.commentsCount || null,
        date: p.timestamp ? relativeDate(p.timestamp) : '',
        permalink: p.permalink || IG_URL,
        isVideo: p.mediaType === 'VIDEO',
      }))
    : INSTAGRAM_POSTS.slice(0, 8).map((p) => ({ ...p, permalink: IG_URL, isVideo: false }));

  const renderProfileHeader = () => (
    <div className="bg-white rounded-t-3xl border border-slate-200 border-b-0 p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="relative flex-shrink-0">
          <div className="w-24 h-24 lg:w-28 lg:h-28 rounded-full p-[3px] bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]">
            <div className="w-full h-full rounded-full bg-white p-[3px]">
              <img
                src={IG_PROFILE.avatar}
                alt={IG_PROFILE.name}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h3 className="text-2xl font-bold text-[#0B3B44]">@{IG_PROFILE.handle}</h3>
            <BadgeCheck className="w-5 h-5 text-[#F55B26]" fill="#F55B26" stroke="white" />
            <a
              href={IG_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all"
            >
              Follow
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
          <div className="flex items-center gap-6 mb-3 text-sm">
            <span className="text-[#0B3B44]"><strong>{IG_PROFILE.posts}</strong> posts</span>
            <span className="text-[#0B3B44]"><strong>{IG_PROFILE.followers}</strong> followers</span>
            <span className="text-[#0B3B44]"><strong>{IG_PROFILE.following}</strong> following</span>
          </div>
          <div className="text-sm">
            <div className="font-bold text-[#0B3B44]">{IG_PROFILE.name}</div>
            <p className="text-slate-700 whitespace-pre-line leading-snug">{IG_PROFILE.bio}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-slate-200 flex items-center justify-center gap-2 text-xs font-semibold text-[#0B3B44] uppercase tracking-widest">
        <Grid3x3 className="w-4 h-4" />
        Posts
      </div>
    </div>
  );

  return (
    <section id="instagram" className="py-24 bg-gradient-to-b from-white to-[#FFF7F1]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white font-semibold text-xs uppercase tracking-widest rounded-full mb-4">
            <InstaIcon className="w-3.5 h-3.5" />
            Latest from Instagram
          </div>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-[#0B3B44] leading-tight">
            Follow our journey, <span className="text-[#F55B26]">post by post.</span>
          </h2>
          <p className="text-lg text-slate-600 mt-3">
            Real progress from our feeding programs, community events, and volunteer stories — straight from our Instagram.
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#F55B26] animate-spin" />
          </div>
        )}

        {!loading && SNAPWIDGET_URL && (
          <div className="rounded-3xl overflow-hidden bg-white shadow-lg border border-slate-100 max-w-5xl mx-auto">
            <iframe
              src={SNAPWIDGET_URL}
              title="Hello Foundation Instagram feed"
              className="w-full block"
              style={{ height: SNAPWIDGET_HEIGHT, border: 0, overflow: 'hidden' }}
              scrolling="no"
              loading="lazy"
              frameBorder="0"
            />
          </div>
        )}

        {!loading && !SNAPWIDGET_URL && (
          <div className="max-w-5xl mx-auto">
            {renderProfileHeader()}

            {hasEmbeds ? (
              <div className="bg-white border border-t-0 border-slate-200 rounded-b-3xl p-4 lg:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {embedUrls.map((url, i) => (
                    <div
                      key={i}
                      className="rounded-2xl overflow-hidden bg-slate-50 border border-slate-100"
                    >
                      <iframe
                        src={url}
                        title={`Instagram post ${i + 1}`}
                        className="w-full"
                        style={{ height: 620, border: 0 }}
                        loading="lazy"
                        scrolling="no"
                        allowFullScreen
                      />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-t-0 border-slate-200 rounded-b-3xl p-2 lg:p-4">
                <div className="grid grid-cols-3 md:grid-cols-4 gap-1 lg:gap-2">
                  {gridPosts.map((post) => (
                    <a
                      key={post.id}
                      href={post.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      onMouseEnter={() => setHovered(post.id)}
                      onMouseLeave={() => setHovered(null)}
                      className="group relative aspect-square overflow-hidden bg-slate-100"
                    >
                      <img
                        src={post.image}
                        alt={post.caption}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {post.isVideo && (
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 backdrop-blur flex items-center justify-center">
                          <Play className="w-3.5 h-3.5 text-white" fill="white" />
                        </div>
                      )}
                      <div
                        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${
                          hovered === post.id ? 'opacity-100' : 'opacity-0'
                        } flex items-center justify-center`}
                      >
                        <div className="flex items-center gap-4 text-white font-bold">
                          {post.likes != null && (
                            <span className="flex items-center gap-1.5">
                              <Heart className="w-4 h-4" fill="white" />
                              {post.likes}
                            </span>
                          )}
                          {post.comments != null && (
                            <span className="flex items-center gap-1.5">
                              <MessageCircle className="w-4 h-4" fill="white" />
                              {post.comments}
                            </span>
                          )}
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}

            <div className="text-center mt-6">
              <a
                href={IG_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[#F55B26] hover:text-[#E14D1F] font-semibold text-sm"
              >
                View all posts on Instagram
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default InstagramFeed;
